import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { exportSyncLogs, listSyncLogs } from "@/lib/sync-logs.functions";
import { SYNC_LOG_ACTIONS, syncLogActionLabel } from "@/lib/sync-logs";
import { listMembers } from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Download, Inbox, RefreshCw, ShieldCheck } from "lucide-react";
import { formatDateTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/integracao-logs")({
  head: () => ({
    meta: [
      { title: "Logs de integração · Admin" },
      { name: "robots", content: "noindex" },
      {
        name: "description",
        content: "Registros de sincronização de cadastros com filtros e exportação conforme LGPD.",
      },
    ],
  }),
  component: IntegracaoLogsPage,
});

const GROUPS = [
  { value: "action", label: "Tipo de evento" },
  { value: "user", label: "Usuário" },
  { value: "day", label: "Dia" },
  { value: "status", label: "Resultado" },
  { value: "direction", label: "Sentido" },
  { value: "none", label: "Sem agrupamento" },
] as const;

const selectClass = "rounded-md border border-input bg-background px-3 py-2 text-sm";

function IntegracaoLogsPage() {
  const listFn = useServerFn(listSyncLogs);
  const exportFn = useServerFn(exportSyncLogs);
  const membersFn = useServerFn(listMembers);

  const [direction, setDirection] = useState<"all" | "inbound" | "outbound">("all");
  const [status, setStatus] = useState<"all" | "ok" | "error" | "skipped" | "ignored">("all");
  const [action, setAction] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [groupBy, setGroupBy] = useState<(typeof GROUPS)[number]["value"]>("action");
  const [purpose, setPurpose] = useState("");
  const [includeTechnicalPayload, setIncludePayload] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const filters = {
    direction,
    status,
    action: action as "all",
    search,
    ...(userId ? { userId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    groupBy,
    limit: 300,
    offset: 0,
  };

  const { data: members } = useQuery({
    queryKey: ["members-for-sync-logs"],
    queryFn: () => membersFn(),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["sync-logs", filters],
    queryFn: () => listFn({ data: filters }),
  });

  const exportMutation = useMutation({
    mutationFn: (format: "csv" | "json") =>
      exportFn({ data: { ...filters, format, purpose, includeTechnicalPayload } }),
    onSuccess: (r) => {
      const blob = new Blob([r.content], { type: r.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportação registrada em nome de ${r.requestedBy} (${r.total} registros).`);
    },
    onError: (e: Error) => toast.error(e.message || "Falha ao exportar registros"),
  });

  function handleExport(format: "csv" | "json") {
    if (purpose.trim().length < 3) {
      toast.error("Informe a finalidade da exportação (exigência da LGPD).");
      return;
    }
    exportMutation.mutate(format);
  }

  const rows = data?.rows ?? [];
  const groups = data?.groups ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-primary sm:text-3xl">
            Logs de integração de cadastros
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Tudo que foi atualizado, revogado ou ignorado na sincronização com a Gestão de
            cadastros, agrupado para leitura rápida.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} /> Atualizar
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          placeholder="Buscar por e-mail, mensagem ou ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className={cn(selectClass, "min-w-0")}
        >
          <option value="">Todos os usuários</option>
          {(members ?? []).map((m: { id: string; full_name?: string | null }) => (
            <option key={m.id} value={m.id}>
              {m.full_name ?? m.id}
            </option>
          ))}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} className={selectClass}>
          <option value="all">Todos os tipos</option>
          {SYNC_LOG_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {syncLogActionLabel(a)}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className={selectClass}
        >
          <option value="all">Todos os resultados</option>
          <option value="ok">ok</option>
          <option value="error">error</option>
          <option value="skipped">skipped</option>
          <option value="ignored">ignored</option>
        </select>
        <select
          value={direction}
          onChange={(e) => setDirection(e.target.value as typeof direction)}
          className={selectClass}
        >
          <option value="all">Entrada e saída</option>
          <option value="inbound">Recebido da Gestão</option>
          <option value="outbound">Enviado para a Gestão</option>
        </select>
        <div className="flex items-center gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-xs text-muted-foreground">até</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
          className={selectClass}
        >
          {GROUPS.map((g) => (
            <option key={g.value} value={g.value}>
              Agrupar por: {g.label}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="flex items-center gap-2 font-medium text-foreground">
          <ShieldCheck className="h-4 w-4 text-primary" /> Exportação com registro (LGPD)
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          E-mails e CPFs são mascarados na exportação. O arquivo inclui quem solicitou, quando e a
          finalidade, e a solicitação fica registrada nos logs de atividade.
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            placeholder="Finalidade da exportação (ex.: auditoria interna de agosto)"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          />
          <div className="flex gap-2">
            <Button onClick={() => handleExport("csv")} disabled={exportMutation.isPending}>
              <Download className="mr-1.5 h-4 w-4" /> CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport("json")}
              disabled={exportMutation.isPending}
            >
              <Download className="mr-1.5 h-4 w-4" /> JSON
            </Button>
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={includeTechnicalPayload}
            onChange={(e) => setIncludePayload(e.target.checked)}
          />
          Incluir detalhes técnicos (payload) — use apenas quando necessário
        </label>
      </div>

      {isLoading ? (
        <div className="h-64 animate-pulse rounded-xl bg-secondary" />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Inbox className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium text-foreground">Nenhum registro encontrado</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Ajuste os filtros ou aguarde a próxima sincronização.
          </p>
        </div>
      ) : groupBy !== "none" ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {data?.count ?? 0} registros no filtro · {groups.length} grupos
          </p>
          {groups.map((g) => {
            const open = openGroup === g.key;
            const groupRows = rows.filter((r) =>
              groupBy === "action"
                ? r.action === g.key
                : groupBy === "status"
                  ? r.status === g.key
                  : groupBy === "direction"
                    ? r.direction === g.key
                    : groupBy === "day"
                      ? r.created_at.slice(0, 10) === g.key
                      : (r.email ?? r.user_id ?? r.external_member_id ?? "sem-identificacao") ===
                          g.key || r.email === g.label,
            );
            return (
              <div key={g.key} className="rounded-xl border border-border bg-card">
                <button
                  type="button"
                  onClick={() => setOpenGroup(open ? null : g.key)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{g.label}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      último em {formatDateTimeBR(g.lastAt)}
                      {g.sampleMessage ? ` · ${g.sampleMessage}` : ""}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-2 text-xs">
                    {g.errors > 0 && (
                      <span className="rounded bg-destructive/15 px-2 py-0.5 font-medium text-destructive">
                        {g.errors} erro(s)
                      </span>
                    )}
                    <span className="rounded bg-secondary px-2 py-0.5 font-semibold">
                      {g.total}
                    </span>
                  </span>
                </button>
                {open && (
                  <ul className="divide-y divide-border border-t border-border">
                    {groupRows.map((r) => (
                      <li key={r.id} className="px-4 py-2 text-xs">
                        <span className="text-muted-foreground">
                          {formatDateTimeBR(r.created_at)}
                        </span>{" "}
                        · {syncLogActionLabel(r.action)} · {r.email ?? r.external_member_id ?? "—"}{" "}
                        · <span className="text-foreground">{r.message ?? "sem detalhes"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Quando</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Sentido</th>
                <th className="px-3 py-2 text-left">Cadastro</th>
                <th className="px-3 py-2 text-left">Resultado</th>
                <th className="px-3 py-2 text-left">Mensagem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                    {formatDateTimeBR(r.created_at)}
                  </td>
                  <td className="px-3 py-2">{syncLogActionLabel(r.action)}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.direction === "inbound" ? "Recebido" : "Enviado"}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.email ?? r.external_member_id ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className={cn(
                        "rounded px-2 py-0.5 text-xs font-medium",
                        r.status === "error"
                          ? "bg-destructive/15 text-destructive"
                          : "bg-primary/10 text-primary",
                      )}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">{r.message ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
